import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatCurrencyValue } from '../../lib/currencyFormatters';
import RefundRadioButton from '../RefundRadioButton';
import { selectRefundOption } from '../../actions/refunds';
import ReviewSectionHeader from '../ReviewSectionHeader';

const RefundOptionsContainerDiv = styled.div`
  padding: 10px 5px 5px 5px;
`;

const RefundOptionDiv = styled.div`
  margin: 10px 0 0 0;
`;

export const mapStateToProps = ({
  page,
  language,
  refunds,
  returnsSettings,
  retailerSettings,
  estimate,
  orderInfo,
}) => ({
  page,
  language,
  refunds,
  returnsSettings,
  retailerSettings,
  estimate,
  orderInfo,
});

const mapTokensInLocalization = ({ localizedString = '', tokens = {} }) =>
  Object.keys(tokens).reduce(
    (output, tokenKey) =>
      output.replace(`$${tokenKey}`, tokens[tokenKey] === null ? '' : tokens[tokenKey]),
    localizedString,
  );

const formatOptions = ({ methods, orderLocale, locale, currency }) =>
  methods.map((option) => {
    const { refund_totals: refundTotals = {}, credits = {}, debits = {} } = option;
    option.itemizations = { ...refundTotals, ...credits, ...debits };
    option.templateVariables = {
      ...option,
      ...Object.keys(option.itemizations).reduce((output, feeKey) => {
        output[feeKey] = formatCurrencyValue({
          value: option.itemizations[feeKey],
          locale: orderLocale || locale,
          currencyCode: currency,
        });
        return output;
      }, {}),
    };
    return option;
  });

class RefundOptions extends React.Component {
  constructor(props) {
    super(props);

    this.state = { formattedOptions: [] };
  }

  componentDidMount = () => {
    const {
      page: { locale },
      refunds: { estimate: { methods = [], currency = '' } = {} } = {},
      orderInfo: { orderLocale } = {},
    } = this.props;

    const formattedOptions = formatOptions({
      methods,
      orderLocale,
      locale,
      currency,
    });

    if (formattedOptions.length === 1) {
      this.updateRefundOptionSelection(formattedOptions[0].type);
    }

    // Formatting of template variables
    this.setState({ formattedOptions });
  };

  componentDidUpdate = () => {
    const {
      page: { locale },
      refunds: { estimate: { methods = [], currency = '' } = {} } = {},
      orderInfo: { orderLocale } = {},
    } = this.props;

    const formattedOptions = formatOptions({
      methods,
      orderLocale,
      locale,
      currency,
    });

    const optionsHaveUpdated = !(
      this.state.formattedOptions.length === formattedOptions.length &&
      this.state.formattedOptions.every((value, index) => value === formattedOptions[index])
    );

    if (optionsHaveUpdated) {
      this.setState({ formattedOptions });
    }
  };

  updateRefundOptionSelection = (selectedType) => {
    const { dispatch } = this.props;
    dispatch(selectRefundOption(selectedType));
  };

  render() {
    const { language } = this.props;
    const { formattedOptions } = this.state;
    const allowOptionSelection = formattedOptions.length > 1;
    const {
      refunds: { selectedType },
    } = this.props;

    return (
      <div
        role="radiogroup"
        aria-labelledby="refund_options_group"
        className="options-list-container"
      >
        {allowOptionSelection ? (
          <ReviewSectionHeader id="refund_options_group">
            {language[`returns.step_3.refund_label`]}
          </ReviewSectionHeader>
        ) : null}
        <RefundOptionsContainerDiv>
          {formattedOptions.map((option) => {
            const {
              type: optionType,
              name_token: nameToken,
              refund_message: refundMessage,
              templateVariables = {},
            } = option;

            if (allowOptionSelection) {
              return (
                <RefundOptionDiv key={optionType} className={`refund-option ${optionType}`}>
                  <RefundRadioButton
                    refundType={optionType}
                    onChangeHandler={this.updateRefundOptionSelection}
                    isChecked={optionType === selectedType}
                    label={
                      refundMessage ||
                      mapTokensInLocalization({
                        localizedString: language[`returns.step_3.${nameToken}.text`],
                        tokens: templateVariables,
                      })
                    }
                  />
                </RefundOptionDiv>
              );
            }
            return (
              <RefundOptionDiv
                key={optionType}
                className={`refund-option ${optionType} single-refund-option`}
                data-test={`refund-option-${optionType}`}
                dangerouslySetInnerHTML={{
                  __html:
                    refundMessage ||
                    mapTokensInLocalization({
                      localizedString: language[`returns.step_3.${nameToken}.text`],
                      tokens: templateVariables,
                    }),
                }}
              />
            );
          })}
        </RefundOptionsContainerDiv>
      </div>
    );
  }
}

RefundOptions.propTypes = {
  page: PropTypes.object.isRequired,
  returnsSettings: PropTypes.object.isRequired,
  refunds: PropTypes.object.isRequired,
  retailerSettings: PropTypes.object.isRequired,
  language: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  orderInfo: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(RefundOptions);
